import PropTypes from 'prop-types';

import { useAppSelector } from '@/redux';
import {
  getAttributeId,
  isProductTtc,
  isPrimeurCode,
  getCurrentItemPrices,
  getAttributeValue,
  getAttributeShortValue
} from '@/utils/productUtils';

import MillesimaPrice from '@/components/common/price/MillesimaPrice';

import styles from './ProductPrice.module.scss';
import { STORE_ID } from '@/constants/stores';
import { HTML_ENTITY } from '@/constants/htmlEntity';

export const ProductPrice = ({
  product,
  currentItem,
  displayUnitBloc = true,
  customClass = '',
  customClassPrice = undefined,
  showVat = true,
  withFormat = false,
  leftSide = false
}) => {
  const isAlert = getAttributeId(product, 'alerte_o_n') === '1';

  const storeId = useAppSelector(({ root }) => root.storeConf?.storeId);
  const isEc = useAppSelector(({ user }) => !!user?.content?.isEc);

  // eslint-disable-next-line camelcase
  if ((product?.exclusive_cellar && !isEc) || isAlert || !product?.display) {
    return null;
  }

  const isTtc = isProductTtc(product, currentItem);
  const displayPriceLiter = [STORE_ID.DEUTSCHLAND, STORE_ID.OSTERREICH].includes(storeId);

  const { priceLiter, unitPrice, listPrice, offerPrice, htPrice } = getCurrentItemPrices(currentItem, product);
  const isReduce = offerPrice !== listPrice && offerPrice > 0;

  const conditionnement = getAttributeShortValue(currentItem, 'conditionnement') || getAttributeShortValue(currentItem, 'format');
  const format = `${getAttributeValue(currentItem, 'nbunit')}${HTML_ENTITY.NO_BREAK_SPACE}x${HTML_ENTITY.NO_BREAK_SPACE}${conditionnement}`;

  const containerClassNames = [
    styles.container,
    isReduce && styles['is-reduce'],
    leftSide && styles['left-side'],
    customClass
  ].filter(Boolean).join(' ');

  const classNamePrice = [
    styles['offer-price'],
    customClassPrice?.listPrice,
    customClassPrice?.offerPrice,
    isReduce && styles['is-reduce']
  ].filter(Boolean).join(' ');

  const unitBloc = (() => displayUnitBloc && (
    <div className={styles['below-price-bloc']}>
      {withFormat && `${format}${HTML_ENTITY.NO_BREAK_SPACE}`}
      (<MillesimaPrice value={displayPriceLiter ? priceLiter : unitPrice}
        unit={{
          display: true,
          type: displayPriceLiter ? 'liter' : 'unit'
        }}
      />)
    </div>
  ))();

  return (
    <div className={containerClassNames} data-test-id="product-price">
      <div>
        <MillesimaPrice
          value={listPrice}
          vat={{
            display: showVat && !isReduce,
            included: isTtc
          }}
          className={{
            container: styles['list-price'],
            value: [customClassPrice?.listPrice, isReduce && styles.strikeout].filter(Boolean).join(' '),
            vat: styles.vat
          }}
        />
        {unitBloc}
      </div>

      {isReduce && (
        <div className={styles['reduced-price']}>
          <MillesimaPrice
            value={offerPrice}
            className={{
              value: classNamePrice,
              vat: styles.vat,
              container: customClassPrice?.listPrice ?? ''
            }}
            vat={{
              display: showVat && isReduce,
              included: isTtc
            }}
          />
        </div>
      )}
      {isTtc && isPrimeurCode(getAttributeId(product, 'in_stock')) && htPrice > 0 && (
        <div className={styles['price-ht']}>
          <MillesimaPrice value={htPrice}
            displayHt={true}
            className={{
              container: customClassPrice?.htPrice ? customClassPrice.htPrice : styles['below-price-bloc']
            }}
          />
        </div>
      )}
    </div>
  );
};

ProductPrice.propTypes = {
  product: PropTypes.object.isRequired,
  currentItem: PropTypes.object.isRequired,
  displayUnitBloc: PropTypes.bool,
  customClass: PropTypes.string,
  isMobile: PropTypes.bool,
  customClassPrice: PropTypes.object,
  showVat: PropTypes.bool
};

export default ProductPrice;
